import React, { useEffect } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import styled from 'styled-components'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'

import Text from '../../ui/Text'
import { WidgetLayout } from '../widget-layout'
import hasClaimForCompany from '../hasClaimForCompany'
import Claims from '../claims'
import { useCompanyGetCompanyByIdQuery, useCompanyUpdateCompanyMutation } from '../../common/redux/api/web-api'
import { notifyError, notifySuccess } from '../../common/features/notificationsManager/Notify'
import Input from '../../common/components/Input'
import { RootState } from '../../admin/redux/store'
import { Button } from '../../common/components/Button'
import TextArea from '../../common/components/TextArea'
import useText from '../../common/hooks/useText'
import {
    MAX_NAME_LENGTH,
    MAX_ORG_EMAIL_LENGTH,
    MAX_ORG_DESC_LENGTH,
    MAX_ORG_PHONE_NO_LENGTH,
    MAX_ORG_CONTACT_PERSON_LENGTH,
    MAX_ORG_ADDRESS_1_LENGTH,
    MAX_ORG_ADDRESS_2_LENGTH,
    MAX_ORG_COMMENT_LENGTH,
} from '../../common/constants/InputsLength'

const SFormsGroups = styled.form`
    display: flex;
    flex-wrap: wrap;
    gap: 35px;
`

const SFormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
    flex-grow: 1;
`

const SActions = styled.div`
    margin: auto 0 0 auto;
`

const SCheckboxForm = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`

const InlineLabelWithCheckBox = styled.label`
    margin-top: 4px;
    margin-bottom: 0px;
`

interface IInputs {
    name: string
    phone: string
    contactPerson: string
    kWhPrice: number
    comment: string
    description: string
    email: string
    address1: string
    address2: string
    ownerReadOnly: boolean
}

export type OrganizationSettingsWidgetProps = {
    className?: ClassName
    style?: React.CSSProperties
}

export const OrganizationSettingsWidget = React.memo(function OrganizationSettingsWidget({
    className,
    style,
}: OrganizationSettingsWidgetProps): React.ReactElement {
    const text = useText()
    const companyId = useSelector((state: RootState) => state.user.selectedCompanyId)
    const user = useSelector((state: RootState) => state.user)

    const [updateCompany, { isLoading: isSaving }] = useCompanyUpdateCompanyMutation()
    const { data: company, isLoading: isLoading, refetch } = useCompanyGetCompanyByIdQuery({ companyId: companyId })

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isValid, isDirty },
    } = useForm<IInputs>({
        defaultValues: {
            name: company?.name ?? '',
            phone: company?.phone ?? '',
            contactPerson: company?.contactPerson ?? '',
            address1: company?.address1 ?? '',
            address2: company?.address2 ?? '',
            kWhPrice: company?.kWhPrice ?? 0,
            comment: company?.comment ?? '',
            description: company?.description ?? '',
            ownerReadOnly: company?.ownerReadOnly ?? false,
            email: company?.email ?? '',
        },
        mode: 'onChange',
        delayError: 500,
    })

    useEffect(() => {
        reset(company)
    }, [company])

    const saveCompany = async (company: IInputs) => {
        try {
            await updateCompany({
                company: {
                    ...company,
                },
            }).unwrap()
            refetch()
            notifySuccess(text('msgCompanyUpdated'))
        } catch (error) {
            notifyError(text('msgCompanyNotUpdated'))
        }
    }

    return (
        <WidgetLayout
            className={cn(className)}
            style={style}
            title={text('lblOrganisationSettingsPage')}
            loadingStatus={isLoading}
            data-testid="org-settings-widget"
        >
            <SFormsGroups noValidate>
                <SFormGroup>
                    <div>
                        <label htmlFor="name" data-testid="name">
                            <Text code="lblNameField" />
                        </label>
                        <Input
                            data-testid="name-input"
                            id="name"
                            {...register('name', {
                                required: {
                                    value: true,
                                    message: text('msgNameRequired'),
                                },
                            })}
                            error={errors && errors.name?.message}
                            maxLength={MAX_NAME_LENGTH}
                            required
                        />
                    </div>
                    <div>
                        <label htmlFor="phone" data-testid="phone">
                            <Text code="lblPhoneField" />
                        </label>
                        <Input id="phone" {...register('phone')} maxLength={MAX_ORG_PHONE_NO_LENGTH} />
                    </div>
                    <div>
                        <label htmlFor="contactPerson" data-testid="contactPerson">
                            <Text code="lblContactPersonField" />
                        </label>
                        <Input
                            id="contactPerson"
                            {...register('contactPerson')}
                            maxLength={MAX_ORG_CONTACT_PERSON_LENGTH}
                        />
                    </div>
                    <div>
                        <label htmlFor="price" data-testid="kWhPrice">
                            <Text code="lblPricePerKWhField" />
                        </label>
                        <Input id="price" type="number" {...register('kWhPrice')} />
                    </div>
                    <div>
                        <label htmlFor="comment" data-testid="comment">
                            <Text code="lblCommentField" />
                        </label>
                        <TextArea
                            id="comment"
                            rows={5}
                            cols={5}
                            {...register('comment')}
                            maxLength={MAX_ORG_COMMENT_LENGTH}
                        />
                    </div>
                </SFormGroup>
                <SFormGroup>
                    <div>
                        <label htmlFor="description" data-testid="description">
                            <Text code="lblDescriptionField" />
                        </label>
                        <Input
                            id="description"
                            data-testid="description-input"
                            {...register('description', {
                                required: {
                                    value: true,
                                    message: text('msgDescriptionRequired'),
                                },
                            })}
                            error={errors && errors.description?.message}
                            maxLength={MAX_ORG_DESC_LENGTH}
                            required
                        />
                    </div>
                    <div>
                        <label htmlFor="email" data-testid="email">
                            <Text code="lblEmailField" />
                        </label>
                        <Input type="email" id="email" {...register('email')} maxLength={MAX_ORG_EMAIL_LENGTH} />
                    </div>
                    <div>
                        <label htmlFor="address1" data-testid="address1">
                            <Text code="lblAddressLineOneField" />
                        </label>
                        <Input id="address1" {...register('address1')} maxLength={MAX_ORG_ADDRESS_1_LENGTH} />
                    </div>
                    <div>
                        <label htmlFor="address2" data-testid="address2">
                            <Text code="lblAddressLineTwoField" />
                        </label>
                        <Input id="address2" {...register('address2')} maxLength={MAX_ORG_ADDRESS_2_LENGTH} />
                    </div>
                    <div>
                        <h5>
                            <Text code="lblReadOnlyAccess" />
                        </h5>
                        <div>
                            <Text code="lblReadOnlyAccessDescription" />
                        </div>

                        <SCheckboxForm>
                            <input type="checkbox" className="checkbox-black" {...register('ownerReadOnly')} />
                            <InlineLabelWithCheckBox htmlFor="ownerReadOnly" data-testid="ownerReadOnly">
                                <h5>{text('lblActivateReadOnlyAccess')}</h5>
                            </InlineLabelWithCheckBox>
                        </SCheckboxForm>
                    </div>
                    {hasClaimForCompany(user, companyId || '', Claims.Admin) && (
                        <SActions>
                            <Button
                                data-testid="save-btn"
                                type="submit"
                                disabled={isLoading || isSaving || !isValid || !isDirty}
                                onClick={handleSubmit(saveCompany)}
                                label={text('btnSave')}
                            />
                        </SActions>
                    )}
                </SFormGroup>
            </SFormsGroups>
        </WidgetLayout>
    )
})
